import { useState } from 'react';
import { API_URL } from '@constants';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';

const ENDPOINT_URL = '/spaces/document';

export interface DeleteSpaceDocumentSuccessResponse {
    status: number;
    success: boolean;
    message: string;
}

/**
 * Requests endpoint to delete a space document.
 */
export const useDeleteSpaceDocument = () => {
    const { getHeaders, handleCommonErrors } = useApiConfig();
    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<number | null>(null);

    const request = async (uuid: string): Promise<DeleteSpaceDocumentSuccessResponse | undefined> => {
        setLoading(true);
        setSuccess(false);
        setSuccessMessage(null);
        setErrorMessage(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}/${uuid}`, {
                method: 'DELETE',
                headers: getHeaders(),
            });

            const json = await response.json();

            if (!response.ok) {
                const error = new Error(json.message || json.error || 'Erro ao eliminar documento') as ApiError;
                error.statusCode = response.status;
                setErrorMessage(json.message || json.error || 'Erro ao eliminar documento');
                throw error;
            }

            const data: DeleteSpaceDocumentSuccessResponse = json;
            setSuccess(true);
            setSuccessMessage(data.message);
            setErrorMessage(null);

            return data;
        } catch (err: any) {
            setSuccess(false);
            handleCommonErrors(err);
        } finally {
            setLoading(false);
            setLastResponseTime(Date.now());
        }
    };

    return {
        request,
        loading,
        success,
        successMessage,
        errorMessage,
        lastResponseTime,
    };
};
